import React, { useState } from 'react';
import { debounce } from 'lodash';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Button,
  Form,
  useApi,
  Dropdown,
  Grid,
  GridColumn,
  GridRow,
} from '@axiom/ui';
import type { Education, Candidate } from '@axiom/validation';

import { EDUCATION_DEGREES } from '../../models/education';
import { DateUtil } from '../../utils/date-util';
import { CandidateApi } from '../../api/candidate-api';
import { LegacyLawSchoolsApi } from '../../api/legacy-law-schools-api';
import { EducationApi } from '../../api/education-api';
import { EducationFormSchema } from '../../schema/education-form-schema';

const year = new Date().getFullYear();
const educationYears = DateUtil.getYearOptionsByRange(year + 10, 1950);

export interface EducationFormProps {
  candidateId: Candidate['id'];
  education?: Education | null;
  onClose: () => void;
  refreshData: () => Promise<void>;
  name: string;
}

export const EducationForm = ({
  candidateId,
  education,
  onClose,
  refreshData,
  name,
}: EducationFormProps) => {
  const [search, setSearch] = useState('');
  const [{ data: candidate }, { data: lawSchoolsResponse }] = useApi(
    CandidateApi.readCandidate(candidateId),
    LegacyLawSchoolsApi.readLawSchools({ query: search ? { search } : {} })
  );
  const hasJdEducation = () =>
    !!candidate.degrees?.some(obj => obj.degree === EDUCATION_DEGREES.JD);

  const degreeOptions = Object.entries(EDUCATION_DEGREES).map(
    ([key, value]) => ({
      key,
      value,
    })
  );
  /**
   * Only filter out JD option if the current form doesn't contain it
   * and if another degree already has it.
   */
  const getDegreeOptions = () => {
    const options =
      hasJdEducation() && education?.degree !== EDUCATION_DEGREES.JD
        ? degreeOptions.filter(option => option.value !== EDUCATION_DEGREES.JD)
        : degreeOptions;

    return options.map(option => ({
      label: option.value,
      value: option.key,
    }));
  };

  return (
    <Form
      name={name}
      schema={EducationFormSchema}
      onSubmit={async (changedData, __, formData) => {
        await (formData.educationId
          ? EducationApi.updateEducation(formData.educationId, changedData)
          : EducationApi.createEducation(candidateId, {
              ...formData,
            }));
        await refreshData();
        onClose();
      }}
      initialValues={{
        candidateId,
        educationId: education?.id,
        lawSchoolId: education?.lawSchoolId,
        degree: education?.degree || undefined,
        yearAwarded: education?.yearAwarded || undefined,
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="EDUCATION_MODAL">
            <ModalHeader
              name="EDUCATION_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              {education?.id ? 'Edit' : 'Add'} Education
            </ModalHeader>
            <ModalSection disableScrolling>
              <Grid>
                <GridRow gutterBottom="16px">
                  <GridColumn>
                    <Dropdown
                      onType={debounce(e => {
                        setSearch(e);
                      }, 250)}
                      displayKey="name"
                      name="lawSchoolId"
                      options={lawSchoolsResponse}
                      valueKey="id"
                      label="Institution"
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>
                    <Dropdown
                      label="Date"
                      options={educationYears}
                      name="yearAwarded"
                      displayKey="label"
                      valueKey="value"
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <Dropdown
                      options={getDegreeOptions()}
                      name="degree"
                      displayKey="label"
                      valueKey="value"
                      label="Degree"
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                name="CANCEL_EDUCATION_BUTTON"
                onClick={() => {
                  onClose();
                }}
                variation="outline"
              >
                Cancel
              </Button>
              <Button name="SAVE_EDUCATION_BUTTON" onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
